// src/contexts/CompanyContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { collection, getDocs, doc, getDoc, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const [timestampFeature, setTimestampFeature] = useState(null);
  const companyAccountUid = activeCompany?.companyAccountUid;

  useEffect(() => {
    setTimestampFeature(null);
    if (!currentUser?.uid || !companyAccountUid) return;

    return onSnapshot(doc(db, "users", companyAccountUid), (snapshot) => {
      setTimestampFeature({
        companyAccountUid,
        userUid: currentUser.uid,
        enabled: snapshot.exists() && snapshot.data().timestamp === true,
        smartAssistanceEnabled: snapshot.exists() && (
          snapshot.data().smartAssistanc === true || snapshot.data().smartAssistance === true
        ),
        reportsEnabled: snapshot.exists() && snapshot.data().reports === true,
        customersAndProjectsEnabled: snapshot.exists() && snapshot.data().customersAndProjects !== false,
        myCompanyEnabled: snapshot.exists() && snapshot.data().myCompany === true,
        adminPagePermissions: snapshot.data()?.adminPagePermissions || {},
      });
    }, (error) => {
      console.error("Kunde inte läsa företagets tillägg och behörigheter:", error);
      setTimestampFeature({ companyAccountUid, userUid: currentUser.uid, enabled: false, accessError: true });
    });
  }, [companyAccountUid, currentUser?.uid]);

  const timestampReady = timestampFeature?.companyAccountUid === companyAccountUid &&
    timestampFeature?.userUid === currentUser?.uid;
  const timestampEnabled = Boolean(currentUser?.uid && timestampReady && timestampFeature?.enabled);
  const timestampLoading = loading || Boolean(currentUser?.uid && companyAccountUid && !timestampReady);
  const reportsEnabled = Boolean(currentUser?.uid && timestampReady && timestampFeature?.reportsEnabled);
  const smartAssistanceEnabled = Boolean(currentUser?.uid && timestampReady && timestampFeature?.smartAssistanceEnabled);
  const reportsLoading = timestampLoading;
  const customersAndProjectsEnabled = Boolean(
    currentUser?.uid && timestampReady && timestampFeature?.customersAndProjectsEnabled
  );
  const myCompanyEnabled = Boolean(currentUser?.uid && timestampReady && timestampFeature?.myCompanyEnabled);

  const permissionsLoading = loading || Boolean(currentUser?.uid && companyAccountUid && !timestampReady);
  const permissionEntry = timestampReady ? Object.values(timestampFeature?.adminPagePermissions || {}).find(
    (entry) => entry.authUid === currentUser?.uid ||
      (entry.email && entry.email === currentUser?.email?.trim().toLowerCase())
  ) : null;
  const canAccessPage = (page) => {
    if (permissionsLoading || !timestampReady || timestampFeature?.accessError) return false;
    if (["customers", "projects"].includes(page) && !customersAndProjectsEnabled) return false;
    if (page === "smartAssistance" && !smartAssistanceEnabled) return false;
    if (page === "myCompany" && !myCompanyEnabled) return false;
    if (["super-admin", "superadmin"].includes(activeCompany?.role)) return true;
    return activeCompany?.role === "admin" && permissionEntry?.[page] !== false;
  };
  const allowedSchemaIds = activeCompany?.role === "admin" && timestampFeature?.accessError
    ? []
    : activeCompany?.role === "admin" && Array.isArray(permissionEntry?.allowedSchemaIds)
      ? permissionEntry.allowedSchemaIds : null;

  const fetchIdRef = useRef(0);

  const normalizeEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    return email.trim().toLowerCase();
  };

  const toEmployedDocId = (email) => {
    return normalizeEmail(email).replace(/\./g, "_");
  };

  const translateRole = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    const roleMap = {
      admin: "Administratör",
      "super-admin": "Super-administratör",
      superadmin: "Super-administratör",
      manager: "Chef",
      user: "Användare",
    };

    return roleMap[normalizedRole] || "Användare";
  };

  const isAdminRole = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    return (
      normalizedRole === "admin" ||
      normalizedRole === "super-admin" ||
      normalizedRole === "superadmin"
    );
  };

  const isActiveMembership = (data = {}, requireExplicitStatus = false) => {
    const status = String(data.membershipStatus || data.status || "")
      .trim()
      .toLowerCase();

    if (data.isActive === false) return false;
    if (!status) return !requireExplicitStatus;
    return status === "active";
  };

  const clearCompanyStorage = () => {
    [
      "selectedCompany",
      "currentCompany",
      "selectedCompanyId",
      "companyId",
      "companyAccountUid",
      "company",
      "activeCompany",
      "activeCompanyId",
    ].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  };

  const saveCompanyToStorage = (company) => {
    if (!company) return;

    const companyJson = JSON.stringify(company);

    localStorage.setItem("selectedCompany", companyJson);
    sessionStorage.setItem("selectedCompany", companyJson);

    localStorage.setItem("currentCompany", companyJson);
    sessionStorage.setItem("currentCompany", companyJson);

    localStorage.setItem("selectedCompanyId", company.companyId);
    sessionStorage.setItem("selectedCompanyId", company.companyId);

    localStorage.setItem("companyId", company.companyId);
    sessionStorage.setItem("companyId", company.companyId);

    localStorage.setItem("companyAccountUid", company.companyAccountUid);
    sessionStorage.setItem("companyAccountUid", company.companyAccountUid);
  };

  const getStoredCompany = () => {
    try {
      const storedCompany =
        localStorage.getItem("selectedCompany") ||
        sessionStorage.getItem("selectedCompany") ||
        localStorage.getItem("currentCompany") ||
        sessionStorage.getItem("currentCompany");

      if (!storedCompany) return null;

      return JSON.parse(storedCompany);
    } catch (error) {
      console.error("Fel vid parsning av sparat företag:", error);
      clearCompanyStorage();
      return null;
    }
  };

  const normalizeCompany = (company) => {
    if (!company) return null;

    const companyId =
      company.companyId || company.companyAccountUid || company.uid || company.id;

    const companyAccountUid =
      company.companyAccountUid || company.companyId || company.uid || companyId;

    return {
      ...company,
      id: company.id || `${companyId}-${company.userDocId || company.email || "company"}`,
      companyId,
      companyAccountUid,
      uid: company.uid || companyAccountUid,
      companyName:
        company.companyName ||
        company.name ||
        company.businessName ||
        "Okänt företag",
      role: String(company.role || "").trim().toLowerCase(),
      roleLabel: company.roleLabel || translateRole(company.role),
    };
  };

  const fetchCompanies = async ({ silent = false } = {}) => {
    const fetchId = fetchIdRef.current + 1;
    fetchIdRef.current = fetchId;

    if (!currentUser?.email) {
      setCompanies([]);
      setLinkedCompanies([]);
      setActiveCompany(null);
      clearCompanyStorage();
      setLoading(false);
      return [];
    }

    if (!silent) setLoading(true);

    try {
      const userEmail = normalizeEmail(currentUser.email);
      const employedDocId = toEmployedDocId(userEmail);

      console.log("=== HÄMTAR FÖRETAG I COMPANYCONTEXT ===");
      console.log("User email:", userEmail);
      console.log("Employed Doc ID:", employedDocId);

      const foundCompanies = [];
      const companiesByKey = new Map();
      const linkedCompaniesByKey = new Map();

      const addCompany = (company) => {
        const normalizedCompany = normalizeCompany(company);

        if (!normalizedCompany?.companyId) return;

        const key = normalizedCompany.companyId;

        if (!linkedCompaniesByKey.has(key) ||
            (!isAdminRole(linkedCompaniesByKey.get(key).role) && isAdminRole(normalizedCompany.role))) {
          linkedCompaniesByKey.set(key, normalizedCompany);
        }

        if (!isAdminRole(normalizedCompany.role)) return;

        if (!companiesByKey.has(key)) {
          companiesByKey.set(key, normalizedCompany);
          foundCompanies.push(normalizedCompany);
        }
      };

      // Metod 1: samma som LoginPage — users där email == användarens email
      const usersRef = collection(db, "users");
      const emailValuesToSearch = Array.from(
        new Set([userEmail, String(currentUser.email || "").trim()])
      ).filter(Boolean);

      for (const emailValue of emailValuesToSearch) {
        const userQuery = query(usersRef, where("email", "==", emailValue));
        const snapshot = await getDocs(userQuery);

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data() || {};
          const role = String(userData.role || "").trim().toLowerCase();

          if (!isActiveMembership(userData)) return;

          const companyId =
            userData.companyId || userData.companyAccountUid || userDoc.id;

          const companyAccountUid =
            userData.companyAccountUid || userData.companyId || userDoc.id;

          addCompany({
            id: `${companyId}-${userDoc.id}`,
            userDocId: userDoc.id,
            companyId,
            companyName:
              userData.companyName ||
              userData.name ||
              userData.businessName ||
              "Okänt företag",
            role,
            roleLabel: translateRole(role),
            email: userEmail,
            companyAccountUid,
            uid: companyAccountUid,
            employedData: userData,
          });
        });
      }

      // Metod 2: gamla strukturen — users/{companyAccountUid}/employed/{email_med_underscore}
      const usersSnapshot = await getDocs(collection(db, "users"));

      for (const userDoc of usersSnapshot.docs) {
        const companyAccountUid = userDoc.id;
        const userData = userDoc.data() || {};

        const employedRef = doc(
          db,
          "users",
          companyAccountUid,
          "employed",
          employedDocId
        );

        const employedSnap = await getDoc(employedRef);

        if (employedSnap.exists()) {
          const employedData = employedSnap.data() || {};
          const role = String(employedData.role || "").trim().toLowerCase();

          if (!isActiveMembership(employedData, true)) continue;

          addCompany({
            id: `${companyAccountUid}-${employedDocId}`,
            userDocId: employedDocId,
            companyId: employedData.companyId || companyAccountUid,
            companyName:
              employedData.companyName ||
              userData.companyName ||
              userData.name ||
              userData.businessName ||
              "Okänt företag",
            role,
            roleLabel: translateRole(role),
            email: userEmail,
            companyAccountUid,
            uid: companyAccountUid,
            employedData,
          });
        }
      }

      if (fetchId !== fetchIdRef.current) {
        return [];
      }

      console.log(
        `Hittade ${foundCompanies.length} företag där användaren är admin/super-admin`
      );

      setCompanies(foundCompanies);
      setLinkedCompanies(Array.from(linkedCompaniesByKey.values()));

      const storedCompany = getStoredCompany();

      if (foundCompanies.length === 0) {
        setActiveCompany(null);
        clearCompanyStorage();
        return [];
      }

      const matchedCompany =
        storedCompany &&
        foundCompanies.find(
          (company) =>
            company.companyId === storedCompany.companyId ||
            company.companyAccountUid === storedCompany.companyAccountUid
        );

      const nextActiveCompany = matchedCompany || foundCompanies[0];

      setActiveCompany(nextActiveCompany);
      saveCompanyToStorage(nextActiveCompany);

      return foundCompanies;
    } catch (error) {
      console.error("Fel vid hämtning av företag:", error);

      if (fetchId === fetchIdRef.current) {
        setCompanies([]);
        setLinkedCompanies([]);
        setActiveCompany(null);
      }

      return [];
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const switchCompany = async (company) => {
    const normalizedCompany = normalizeCompany(company);

    if (!normalizedCompany) {
      setActiveCompany(null);
      clearCompanyStorage();
      return;
    }

    console.log("Byter till företag:", normalizedCompany.companyName);

    setActiveCompany(normalizedCompany);
    saveCompanyToStorage(normalizedCompany);
  };

  const clearCompany = () => {
    setCompanies([]);
    setLinkedCompanies([]);
    setActiveCompany(null);
    clearCompanyStorage();
  };

  useEffect(() => {
    fetchIdRef.current += 1;

    if (currentUser?.email) {
      fetchCompanies();
    } else {
      setCompanies([]);
      setLinkedCompanies([]);
      setActiveCompany(null);
      clearCompanyStorage();
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.email]);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        linkedCompanies,
        activeCompany,
        currentCompany: activeCompany,
        selectedCompany: activeCompany,
        loading,
        timestampEnabled,
        timestampLoading,
        reportsEnabled,
        reportsLoading,
        smartAssistanceEnabled,
        customersAndProjectsEnabled,
        myCompanyEnabled,
        permissionsLoading,
        canAccessPage,
        allowedSchemaIds,
        adminPagePermissions: timestampReady ? timestampFeature?.adminPagePermissions || {} : {},
        fetchCompanies,
        switchCompany,
        clearCompany,
        setActiveCompany,
        setSelectedCompany: setActiveCompany,
        setCurrentCompany: setActiveCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};
